// What an extension is. Two kinds, and the difference matters:
//   Official   made by TrueChops and shipped with the site (plugins/school, plugins/lessons): full
//              plugins, trusted as the site's own code. The list is in official.js.
//   Community  written by anyone, in JavaScript, and published here: one file, run in a sandbox with
//              no access to the page, the account, cookies or (unless allowed) the internet. It can
//              only do what the guest API allows (src/components/extensions/guest.js), and each
//              permission below is agreed to when it's added. A person reviews every version before
//              it's listed.
import { fail, string } from "../social/validation";

export const PERMISSIONS = {
  "score.read": "See the score you have open",
  "score.write": "Change the score you have open (you can undo it)",
  storage: "Keep its own settings on your account",
  network: "Send data to the addresses listed",
};
export const CATEGORIES = { notation: "Notation", practice: "Practice", ideas: "Ideas", daw: "DAW", teaching: "Teaching", fun: "Just for fun" };
// Where an extension's buttons can go (each is an area, src/platform/parts.js, or a slot of its own).
export const BUTTON_PLACES = ["score.toolbar", "score.top", "daw.transport", "map.controls", "feed.top"];
export const EXTENSION_LIMITS = { code: 200000, name: 50, summary: 140, about: 4000, mine: 20, hosts: 5, dataKeys: 100, dataBytes: 100000, perPage: 24 };

const VERSION = /^\d{1,4}\.\d{1,4}\.\d{1,4}$/, HOST = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/;
export const isVersion = (value) => VERSION.test(value || "");
// Above zero when `a` comes after `b`.
export function compareVersions(a, b) {
  const [left, right] = [a, b].map((version) => String(version || "0.0.0").split(".").map(Number));
  return left[0] - right[0] || left[1] - right[1] || left[2] - right[2];
}
export const slugOf = (name) => String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/^(\d)/, "x$1").slice(0, 40);

export function syntaxProblem(code) {
  // Only read, never run: a Function is compiled here and thrown away.
  try { new Function(code); return null; } catch (error) { return error.message; }
}

// An extension as its author sends it, checked.
export function normalizeExtension(body = {}) {
  const permissions = [...new Set((Array.isArray(body.permissions) ? body.permissions : []).filter((id) => Object.hasOwn(PERMISSIONS, id)))];
  const hosts = [...new Set((Array.isArray(body.hosts) ? body.hosts : []).map((host) => String(host).trim().toLowerCase()).filter(Boolean))];
  if (hosts.length > EXTENSION_LIMITS.hosts) fail(400, `An extension can reach up to ${EXTENSION_LIMITS.hosts} addresses.`);
  const wrong = hosts.find((host) => !HOST.test(host));
  if (wrong) fail(400, `“${wrong}” isn't an address like api.example.com.`);
  if (hosts.length && !permissions.includes("network")) permissions.push("network");
  const code = typeof body.code === "string" ? body.code : "";
  if (!code.trim()) fail(400, "Write the extension's code first.");
  if (code.length > EXTENSION_LIMITS.code) fail(413, `An extension can be up to ${EXTENSION_LIMITS.code.toLocaleString("en-US")} characters.`);
  return { name: string(body.name, EXTENSION_LIMITS.name, "name", true), summary: string(body.summary, EXTENSION_LIMITS.summary, "summary", true),
    about: typeof body.about === "string" ? body.about.trim().slice(0, EXTENSION_LIMITS.about) : "", category: Object.hasOwn(CATEGORIES, body.category) ? body.category : "fun", permissions, hosts, code };
}

// What a reviewer should look at: not proof of anything, only where to look first.
export function reviewNotes(code, { permissions = [], hosts = [] } = {}) {
  const notes = [], says = (pattern) => pattern.test(code);
  if (says(/\beval\s*\(|new\s+Function\b/)) notes.push("Builds code from text (eval or new Function).");
  if (says(/\b(fetch|XMLHttpRequest|WebSocket|EventSource|sendBeacon)\b/) && !hosts.length) notes.push("Mentions the network but lists no addresses, so the sandbox will stop it.");
  if (says(/\batob\s*\(|[A-Za-z0-9+/=]{400,}/)) notes.push("Has a long encoded string: check what it decodes to.");
  if (says(/truechops\.score\.(set|replace)/) && !permissions.includes("score.write")) notes.push("Changes the score without asking for score.write, so that call will fail.");
  if (says(/truechops\.storage\./) && !permissions.includes("storage")) notes.push("Uses storage without asking for it, so that call will fail.");
  if (hosts.length) notes.push(`Can send data to: ${hosts.join(", ")}.`);
  return notes;
}

// A starting point for the editor.
export const STARTER = `// Your extension runs in a sandbox. Everything it can do is on \`truechops\`.
// A technique shows up in the idea button on bars.
truechops.technique({
  id: "double-hats",
  name: "Double the hats",
  does: "Adds a hi-hat between the hi-hats",
  kinds: ["kit"],
  run({ events }) {
    const hats = events.filter((event) => event.drum === "hihat");
    return [{ name: "Busier hats", events: [...events, ...hats.map((event) => ({ ...event, at: event.at + 0.25 }))] }];
  },
});

// A button on the Score's toolbar. score.get needs the score.read permission.
truechops.button({
  place: "score.toolbar",
  label: "Count notes",
  async onClick() {
    const { score } = await truechops.score.get();
    const notes = score.measures.flatMap((bar) => bar.parts.flatMap((part) => part.voices.flatMap((voice) => voice.notes))).filter((note) => note.notes.length);
    truechops.toast(notes.length + " notes in this score");
  },
});
`;
